/**
 * Eval session run pacing — controls how aggressively `scheduleEvalRuns`
 * stages evaluation runs.
 *
 * Default is `balanced` (~60 runs/min): Anthropic's post-June-2026 Build/Scale
 * tiers (5M+ ITPM / 1M+ OTPM, and cache reads exempt from ITPM) leave ample room
 * for this alongside production traffic on a shared org-scoped key (provider rate
 * limits are org-scoped, not key-scoped, so the same key serving prod
 * conversations shares a budget with eval traffic).
 *
 * `conservative` (~20 runs/min) is the safe opt-out — use it on keys shared with
 * heavy live production traffic, on tighter providers (Gemini / low-tier OpenAI),
 * or on Start-tier orgs. `fast` (~200 runs/min) is opt-in for high-tier providers
 * off-peak. The 429 retry/backoff on the turn path (`MAX_TURN_RETRIES` +
 * Retry-After reschedule) makes overshoot self-correcting rather than fatal, which
 * is what makes a faster default low-risk.
 */

export const EVAL_PACING_PRESETS = {
  conservative: { base_delay_ms: 5000, run_interval_ms: 3000 }, // ~20 runs/min
  balanced: { base_delay_ms: 2000, run_interval_ms: 1000 }, // ~60 runs/min
  fast: { base_delay_ms: 1000, run_interval_ms: 300 }, // ~200 runs/min
} as const;

// Single source of truth for the valid preset names — drives both the
// `EvalPacingPreset` type and the Zod enum in contracts/evals.ts. `custom` has
// no EVAL_PACING_PRESETS entry (its interval is user-supplied at runtime).
export const EVAL_PACING_PRESET_NAMES = ['conservative', 'balanced', 'fast', 'custom'] as const;

export type EvalPacingPreset = (typeof EVAL_PACING_PRESET_NAMES)[number];

// Typed as a named preset (never 'custom') so it can index EVAL_PACING_PRESETS.
export const DEFAULT_PACING_PRESET: Exclude<EvalPacingPreset, 'custom'> = 'balanced';

export const PACING_INTERVAL_MIN_MS = 50;
export const PACING_INTERVAL_MAX_MS = 60000;

/**
 * Hard ceiling on the total runs a single eval session may materialize
 * (Σ `number_of_runs` over a set's enabled scenarios). Per-scenario runs are
 * capped at 100 (create/update eval), but the scenario *count* is unbounded —
 * so without this a large set can materialize a session bigger than one
 * dispatch invocation can deliver. `scheduleEvalRuns` fans out from a single
 * Worker invocation making 2 ConversationDO RPCs per run; 1000 runs ≈ 2000
 * subrequests, comfortably under the 10k/invocation default with ~5× headroom,
 * and keeps the HubDO watchdog's never-started deadline (`total_runs × pacing
 * + grace + run_deadline`) bounded. Enforced server-side at session run
 * (reject, never clamp — silent truncation would misreport coverage).
 */
export const EVAL_MAX_SESSION_RUNS = 1000;

/**
 * Per-run finalization deadline for the HubDO eval watchdog (gh #2591). A run
 * idle past this window (`now - eval_session_run.updated_at`) is reaped to
 * `failed`. The lease clock measures INACTIVITY, not total turn duration: it's
 * renewed at turn start, on each tool call within the agentic loop, and on every
 * rate-limit retry reschedule — so a legitimately long multi-step turn (many
 * LLM/tool calls) or a retrying run is never falsely reaped; only a genuinely
 * stalled turn (no progress — a tool/LLM call that never returns, a DO reset
 * mid-turn) trips it. The lease is renewed before AND after each tool call, so
 * the gap between two renewals is a single in-flight operation: one LLM round
 * (bounded at the 120s stream timeout) or one tool call. The default sits above
 * that 120s ceiling so a normal LLM round always clears. The residual is a
 * single tool call that itself exceeds the deadline — external tool fetches
 * currently have no execution timeout — which would reap a still-running turn;
 * the generous default + per-session `session_config.run_deadline_ms` override
 * are the mitigation until tool fetches are bounded.
 */
export const EVAL_DEFAULT_RUN_DEADLINE_MS = 180_000;

/**
 * Buffer added on top of the computed pacing-dispatch span when deciding a run
 * that never started (`started_at IS NULL`) is overdue. The never-started
 * deadline is `session.started_at + dispatchSpan + grace + run_deadline`, where
 * `dispatchSpan` is derived from `total_runs × pacing` — so a legitimately
 * not-yet-dispatched paced run is never reaped. (A future per-run `dispatched_at`
 * makes this per-run-precise; this session-level estimate is correct meanwhile.)
 */
export const EVAL_SESSION_DISPATCH_GRACE_MS = 60_000;

/** Clamp bounds for a session-configured `run_deadline_ms`. */
export const EVAL_RUN_DEADLINE_MIN_MS = 1_000;
export const EVAL_RUN_DEADLINE_MAX_MS = 1_800_000; // 30 min

export interface PacingConfig {
  preset: EvalPacingPreset;
  custom_interval_ms?: number;
}

export interface ResolvedPacing {
  base_delay_ms: number;
  run_interval_ms: number;
}

export function resolvePacing(config: PacingConfig | null | undefined): ResolvedPacing {
  if (!config) {
    return EVAL_PACING_PRESETS[DEFAULT_PACING_PRESET];
  }
  if (config.preset === 'custom') {
    const raw = typeof config.custom_interval_ms === 'number' ? config.custom_interval_ms : EVAL_PACING_PRESETS.conservative.run_interval_ms;
    const clamped = Math.min(PACING_INTERVAL_MAX_MS, Math.max(PACING_INTERVAL_MIN_MS, Math.floor(raw)));
    return { base_delay_ms: EVAL_PACING_PRESETS.conservative.base_delay_ms, run_interval_ms: clamped };
  }
  // Named preset lookup. `?? conservative` defends against a malformed runtime
  // value (e.g. an unknown preset string that bypassed contract validation).
  return EVAL_PACING_PRESETS[config.preset] ?? EVAL_PACING_PRESETS.conservative;
}
